import React, { useEffect, useRef, useState } from 'react';
import cipherLogo from '../assets/cipher-icon-only-transparent.png';
import { colors as theme } from '../theme';
import { describeStartup, signInLockState } from '../utils/startupTrace';

const colors = {
  primary: theme.gold,
  bg: theme.bg,
  text: theme.text,
  muted: theme.textMuted,
};

// Seconds to wait before each automatic try. The last one repeats.
export const RETRY_AFTER_SECONDS = [5, 10, 20, 30];
// A try still under way after this long is counted as failed, so "Try again" never stays disabled,
// and the screen offers to reload the app (15 October 2026: a try that never ended left the button
// disabled for good; docs/reviews/nutrition-to-fitness-freeze.md).
export const TRY_GIVES_UP_AFTER_SECONDS = 20;

/**
 * Shown in place of the welcome screen when the person is signed in but the server did not answer
 * (see utils/reachability.js). They have not been signed out, and it says so.
 *
 * It keeps trying by itself, a little less often each time, and at once when the device reports
 * it is back online or the app is brought back to the front. It does not try while the app is in
 * the background. `onRetry` is the check to run again; when it works the app moves on and this
 * screen goes away.
 *
 * `onReload`, if given, is offered once a try has hung: reloading the page starts the sign-in
 * library afresh and keeps the saved sign-in.
 */
const ServerUnreachable = ({ onRetry, onUseAnotherAccount, onReload }) => {
  const [trying, setTrying] = useState(false);
  const [hung, setHung] = useState(false);
  // What start-up is waiting for, in small print, so a screenshot says where it is stuck
  const [details, setDetails] = useState('');
  useEffect(() => {
    let lock = null;
    let alive = true;
    const refresh = async () => {
      lock = await signInLockState();
      if (alive) setDetails(describeStartup(Date.now(), lock));
    };
    refresh();
    const every = setInterval(refresh, 2000);
    return () => { alive = false; clearInterval(every); };
  }, []);
  const retry = useRef(onRetry);
  retry.current = onRetry;
  const failures = useRef(0);
  const busy = useRef(false);
  const timer = useRef(null);
  const shown = useRef(true);
  const tryNow = useRef(() => {});

  useEffect(() => {
    shown.current = true;

    const waitThenTry = () => {
      clearTimeout(timer.current);
      if (document.hidden) return;
      const seconds = RETRY_AFTER_SECONDS[Math.min(failures.current, RETRY_AFTER_SECONDS.length - 1)];
      timer.current = setTimeout(() => tryNow.current(), seconds * 1000);
    };

    tryNow.current = async () => {
      if (busy.current) return;
      busy.current = true;
      clearTimeout(timer.current);
      setTrying(true);
      let giveUp;
      try {
        const outcome = await Promise.race([
          Promise.resolve().then(() => retry.current()).then(() => 'done'),
          new Promise((resolve) => { giveUp = setTimeout(() => resolve('hung'), TRY_GIVES_UP_AFTER_SECONDS * 1000); }),
        ]);
        if (outcome === 'hung' && shown.current) setHung(true);
      } catch {
        // still not reachable
      } finally {
        clearTimeout(giveUp);
      }
      busy.current = false;
      if (!shown.current) return;
      failures.current += 1;
      setTrying(false);
      waitThenTry();
    };

    const onVisibility = () => {
      if (document.hidden) clearTimeout(timer.current);
      else tryNow.current();
    };
    const onOnline = () => tryNow.current();

    waitThenTry();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      shown.current = false;
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100dvh',
      background: colors.bg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      textAlign: 'center',
      fontFamily: "'Inter', sans-serif",
    }}>
      <img src={cipherLogo} alt="Cipher" style={{ width: '96px', height: '96px', marginBottom: '24px' }} />
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: colors.text, margin: '0 0 12px' }}>
        Can't reach the server
      </h1>
      <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.6, maxWidth: '360px', margin: '0 0 8px' }}>
        You're still signed in. We can't load your account right now, so we'll keep trying.
        You don't need to log in again.
      </p>
      <p role="status" style={{ fontSize: '14px', color: colors.muted, minHeight: '21px', margin: '0 0 24px' }}>
        {trying ? 'Trying now…' : 'Trying again shortly.'}
      </p>
      <button
        onClick={() => tryNow.current()}
        disabled={trying}
        style={{
          width: '100%',
          maxWidth: '320px',
          padding: '16px',
          background: `linear-gradient(135deg, ${colors.primary}, ${theme.goldPressed})`,
          border: 'none',
          borderRadius: '14px',
          color: theme.bg,
          fontSize: '16px',
          fontWeight: 600,
          cursor: trying ? 'default' : 'pointer',
          opacity: trying ? 0.6 : 1,
        }}
      >
        Try again
      </button>
      {hung && onReload && (
        <div style={{ marginTop: '16px', maxWidth: '320px' }}>
          <p style={{ fontSize: '14px', color: colors.muted, lineHeight: 1.5, margin: '0 0 10px' }}>
            Still stuck? Reloading the app usually clears it. You'll stay signed in.
          </p>
          <button
            onClick={() => onReload()}
            style={{
              width: '100%', padding: '14px', background: 'transparent', border: `1px solid ${theme.surface3}`,
              borderRadius: '14px', color: colors.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Reload Cipher
          </button>
        </div>
      )}
      {details && (
        <p style={{ fontSize: '11px', color: colors.muted, opacity: 0.8, margin: '20px 0 0', maxWidth: '360px', lineHeight: 1.4 }}>
          {details}
        </p>
      )}
      {onUseAnotherAccount && (
        <button
          onClick={onUseAnotherAccount}
          style={{
            marginTop: '20px',
            padding: '8px',
            background: 'none',
            border: 'none',
            color: colors.muted,
            fontSize: '14px',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Log in with a different account
        </button>
      )}
    </div>
  );
};

export default ServerUnreachable;
